import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { Database, Info, Loader2, PanelRightClose } from "lucide-react";
import { AronApiBaseContext, useAronMessages } from "@/features/aron/api/aronApi";
import { AronChatView } from "@/features/aron/components/AronChatView";
import { useAronChat } from "@/features/aron/hooks/useAronChat";
import { pdfErrorText, useEnsurePdfChat, usePdfChatInfo, useSetCompanyData } from "../api/pdfApi";

/**
 * Czat asystenta PDF obok pulpitu.
 *
 * Cała maszyneria rozmowy (strumień, załączniki, koszty, stop) jest ta sama co
 * w ARON-ie — pod adresem /pdf, gdzie backend przypina personę „pdf”. Tutaj
 * dochodzi jedno: przełącznik „Dane z 5-tki”, widoczny wyłącznie dla osób
 * z modułem ARON. Bez niego asystent NIE MA mostu do danych firmy.
 */
function ChatInner({
  sessionId,
  conversationId,
  onConversation,
  onTurnFinished,
  onCollapse,
}: {
  sessionId: string;
  conversationId: string | null;
  onConversation: (id: string) => void;
  onTurnFinished: () => void;
  onCollapse: () => void;
}) {
  const { data: info } = usePdfChatInfo(sessionId);
  const setCompanyData = useSetCompanyData();
  const [switchError, setSwitchError] = useState<string | null>(null);

  const { sending, handleSend, handleStop } = useAronChat(
    conversationId,
    (id) => id && onConversation(id),
    { session_id: sessionId },
  );

  // Po każdej skończonej turze asystenta pulpit może wyglądać inaczej (nowe
  // wersje, nowe pliki) — odświeżamy go dokładnie wtedy, a nie w pętli.
  const { data: messages = [] } = useAronMessages(conversationId);
  const lastStatus = useRef<string | null>(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const status = last?.role === "assistant" ? last.status ?? null : null;
    if (lastStatus.current && lastStatus.current !== status && status === "done") {
      onTurnFinished();
    }
    lastStatus.current = status;
  }, [messages, onTurnFinished]);

  const canSwitch = !!info?.can_use_company_data;
  const on = !!info?.use_company_data;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label="Dane z 5-tki"
            disabled={!canSwitch || setCompanyData.isPending}
            title={
              canSwitch
                ? "Asystent czyta dane z systemu w granicach Twoich uprawnień (tylko odczyt)"
                : "Wymaga dostępu do modułu ARON (DLC_03)"
            }
            onClick={() => {
              setSwitchError(null);
              setCompanyData.mutate(
                { sessionId, enabled: !on },
                { onError: (e) => setSwitchError(pdfErrorText(e, "Nie udało się przestawić.")) },
              );
            }}
            className={clsx(
              "relative h-5 w-9 shrink-0 border transition-colors duration-100",
              on
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]"
                : "border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]",
              !canSwitch && "cursor-not-allowed opacity-40",
            )}
          >
            <span
              className={clsx(
                "absolute top-0.5 h-3.5 w-3.5 transition-all duration-100",
                on ? "left-[18px] bg-white" : "left-0.5 bg-[var(--color-border-strong)]",
              )}
            />
          </button>
          <span className="flex min-w-0 items-center gap-1.5">
            <Database
              size={13}
              className={on ? "text-[var(--color-primary)]" : "text-[var(--color-text-muted)]"}
            />
            <span className="truncate font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-secondary)]">
              Dane z 5-tki
            </span>
            {setCompanyData.isPending && (
              <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Zwiń czat"
          className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          <PanelRightClose size={16} />
        </button>
      </div>

      {switchError && (
        <p className="border-b border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-3 py-1.5 text-[11px] text-[var(--color-text-primary)]">
          {switchError}
        </p>
      )}

      {/* Zanim odpowiedź o uprawnieniach dojedzie, NIE straszymy brakiem dostępu —
          inaczej osoba z ARON-em widzi przez moment „nie masz dostępu”. */}
      {info && !canSwitch && (
        <p className="flex items-start gap-1.5 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px] text-[var(--color-text-muted)]">
          <Info size={12} className="mt-px shrink-0" />
          Asystent pracuje na plikach tej sprawy. Dostęp do danych firmy mają osoby
          z modułem ARON.
        </p>
      )}

      <div className="min-h-0 flex-1">
        <AronChatView
          key={conversationId ?? "new"}
          conversationId={conversationId}
          sending={sending}
          onSend={handleSend}
          onStop={handleStop}
          showHeader={false}
        />
      </div>
    </div>
  );
}

export function PdfChatPanel({
  sessionId,
  onTurnFinished,
  onCollapse,
}: {
  sessionId: string;
  onTurnFinished: () => void;
  onCollapse: () => void;
}) {
  const { data: info } = usePdfChatInfo(sessionId);
  const ensure = useEnsurePdfChat();
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Wątek zakładamy raz na sprawę i przypinamy do niej — dzięki temu czat i pliki
  // nigdy się nie rozjeżdżają, a wracając do sprawy widać całą wcześniejszą rozmowę.
  useEffect(() => {
    setConversationId(null);
  }, [sessionId]);

  useEffect(() => {
    if (!info) return;
    if (info.conversation_id) {
      setConversationId(info.conversation_id);
    } else if (!ensure.isPending) {
      ensure.mutate(sessionId, { onSuccess: (d) => setConversationId(d.conversation_id) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.conversation_id, sessionId]);

  return (
    <AronApiBaseContext.Provider value="/pdf">
      <ChatInner
        sessionId={sessionId}
        conversationId={conversationId}
        onConversation={setConversationId}
        onTurnFinished={onTurnFinished}
        onCollapse={onCollapse}
      />
    </AronApiBaseContext.Provider>
  );
}
